import { isNil } from "lodash";

import { ThousandSeparatorFormatNames } from "../../../../constants/dateFormats";
import { DateOrderIdentifiers } from "../../../../constants/dateOrders";
import calendarTypes from "../../../../types/calendarTypes";
import Precision from "../../../../types/datePrecision";
import { TimespanRecord } from "../../../../types/immutableRecords/model/data/TimespanRecord";
import {
  getErasFromCalendar,
  getHoursInDayFromCalendar,
  getLeapDurationForMonth,
  getMonthsFromCalendar,
  getNormalDurationForMonth,
  getWeekdaysFromCalendar,
  isLeapYearFromCalendar
} from "../selectors";
import dateByAddingSpan from "./dateByAddingSpan";
import { calendarRegularExpressions, getFirstMonthOfYear } from "./dateInternalCalculations";
import dateWithComponents from "./dateWithComponents";
import dateWithTimestamp from "./dateWithTimestamp";
import replaceWithSpaces from "./replaceWithSpaces";
import { isTimespanZero, timespanForPrecision } from "./timespanFunctions";

export const adjustEndDateWhenSave = (calendar, endDate, startDate, duration, forceToEnd = false) => {
  if (endDate) {
    // If the date includes time information, there is nothing to adjust
    if (endDate.precision > Precision.Day) {
      return endDate;
    }

    // This last clause (looking at duration) added by Yang as part of the calendar picker
    // I m not sure I understand what it is doing yet, so just preserving the same behaviour
    // for now.
    const shouldMoveToEnd =
      forceToEnd ||
      (startDate && startDate.timestamp !== endDate.timestamp) ||
      (!startDate && duration && !isTimespanZero(duration));

    if (shouldMoveToEnd) {
      const result = dateByAddingSpan(calendar, endDate, timespanForPrecision(endDate.precision));
      return result;
    }
  }
  return endDate;
};

export const endDateWithString = (calendar, str, startDate, duration, forceToEnd = false) => {
  // First, calculate the date as it is written
  const date = dateWithString(calendar, str);
  // If the precision includes time, or the date matches the start date, we don't need to add anything.
  return adjustEndDateWhenSave(calendar, date, startDate, duration, forceToEnd);
};

/**
 *
 * @param {CalendarRecord} calendar
 * @param {string} str
 */
export const dateWithString = (calendar, str) => {
  // TODO: Add support for floating date formats
  return calendar.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME
    ? absoluteDateWithString(calendar, str)
    : relativeDateWithString(calendar, str);
};

const absoluteDateWithString = (calendar, str) => {
  // Extract era
  const eras = getErasFromCalendar(calendar);
  const autoCorrection = calendar.defaultToModernCentury;

  let remainingStr = str;
  const eraResult = extractEraFromString(calendar, remainingStr);

  const era = eraResult ? eraResult.eraIndex : eras.size - 1;
  remainingStr = eraResult ? eraResult.remainingStr : remainingStr;

  // Extract time
  const timeResult = extractTimeFromString(calendar, remainingStr);
  remainingStr = timeResult ? timeResult.remainingStr : remainingStr;

  // do not auto correct when explicitly giving era
  const shouldAutoCorrect = autoCorrection && !eraResult;
  // Extract date
  const dateResult = extractDateFromString(calendar, remainingStr, shouldAutoCorrect, era, eraResult);
  remainingStr = dateResult ? dateResult.remainingStr : remainingStr;
  if (!dateResult) {
    return null;
  }

  // Return if there's any extra text that isn't a weekday name or punctuation
  remainingStr = typeof remainingStr === "string" ? remainingStr : "";
  remainingStr = remainingStr.replace(/,/, "");
  remainingStr = remainingStr.replace(/\./, "");
  remainingStr = remainingStr.replace(/\+/, "");
  if (remainingStr) {
    const weekdays = getWeekdaysFromCalendar(calendar);
    weekdays.map(weekday => {
      let weekdayRegEx = new RegExp(weekday.name, "ig");
      let weekdayRegEx2 = new RegExp(weekday.shortName, "ig");
      remainingStr = remainingStr.replace(weekdayRegEx, "");
      remainingStr = remainingStr.replace(weekdayRegEx2, "");
    });

    const trimmedRemainingStr = remainingStr.trim();
    if (trimmedRemainingStr) {
      return null;
    }
  }

  const year = dateResult.year;
  const month = dateResult.precision > Precision.Year ? dateResult.month : getFirstMonthOfYear(calendar, era, year);
  const day = dateResult.precision > Precision.Month ? dateResult.day : 1;
  let precision = dateResult.precision;
  let hour = 0;
  let minute = 0;
  let second = 0;
  let addDay = false;

  if (dateResult.precision === Precision.Day && timeResult) {
    hour = timeResult.hour;
    minute = timeResult.minute;
    second = timeResult.second;
    precision = timeResult.precision;
    if (hour === getHoursInDayFromCalendar(calendar).size && minute === 0 && second === 0) {
      addDay = true;
      hour = 0;
    }
  }

  const date = dateWithComponents(calendar, era, year, month, day, hour, minute, second, precision);

  if (addDay) {
    return dateByAddingSpan(calendar, date, timespanForPrecision(Precision.Day));
  }
  return date;
};

const relativeDateWithString = (calendar, str) => {
  if (calendar.calendarType === calendarTypes.CALENDAR_TYPE_HOUR_TIME) {
    const timeResult = extractRelativeTimeFromString(calendar, str);
    if (timeResult) {
      const negative = timeResult.negative;
      const hours = timeResult.hour * (negative ? -1 : 1);
      const minutes = timeResult.minute * (negative ? -1 : 1);
      const seconds = timeResult.second * (negative ? -1 : 1);
      const precision = timeResult.precision;
      const timespan = TimespanRecord({
        hours,
        minutes,
        seconds
      });
      const resultDate = dateByAddingSpan(
        calendar,
        dateWithTimestamp(calendar, calendar.zeroDateTimestamp).set("precision", precision),
        timespan
      );
      return resultDate;
    }
    return null;
  } else if (calendar.calendarType === calendarTypes.CALENDAR_TYPE_DAY_TIME) {
    let remainingStr = str;
    const timeResult = extractTimeFromString(calendar, remainingStr);
    remainingStr = timeResult ? timeResult.remainingStr : remainingStr;
    const dayResult = extractDayFromString(calendar, remainingStr);
    if (dayResult) {
      const days = dayResult.day;
      let precision = Precision.Day;
      let hours = 0;
      let minutes = 0;
      let seconds = 0;
      if (timeResult) {
        hours = timeResult.hour;
        minutes = timeResult.minute;
        seconds = timeResult.second;
        precision = timeResult.precision;
      }
      const timespan = TimespanRecord({ days, hours, minutes, seconds });
      const resultDate = dateByAddingSpan(
        calendar,
        dateWithTimestamp(calendar, calendar.zeroDateTimestamp).set("precision", precision),
        timespan
      );
      return resultDate;
    }
    return null;
  } else if (calendar.calendarType === calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME) {
    let remainingStr = str;
    const timeResult = extractTimeFromString(calendar, remainingStr);
    remainingStr = timeResult ? timeResult.remainingStr : remainingStr;
    const weekdayResult = extractWeekdayFromPartialString(calendar, remainingStr);
    remainingStr = weekdayResult ? weekdayResult.remainingStr : remainingStr;
    const weekResult = extractWeekFromString(calendar, remainingStr);
    if (weekResult || weekdayResult || timeResult) {
      let precision = Precision.Week;
      let weeks = 0;
      let days = 0;
      let hours = 0;
      let minutes = 0;
      let seconds = 0;
      if (weekResult) {
        weeks = weekResult.week;
      }

      const zeroDate = dateWithTimestamp(calendar, calendar.zeroDateTimestamp).set("precision", precision);

      if (weekdayResult) {
        const weekdaysCount = calendar.weekdays.size;
        days = (weekdaysCount + weekdayResult.weekdayIndex - zeroDate.weekday) % weekdaysCount;
        precision = Precision.Day;
      }

      if (timeResult) {
        hours = timeResult.hour;
        minutes = timeResult.minute;
        seconds = timeResult.second;
        precision = timeResult.precision;
      }

      const timespan = TimespanRecord({ weeks, days, hours, minutes, seconds });
      const resultDate = dateByAddingSpan(calendar, zeroDate, timespan);
      return resultDate;
    }
    return null;
  }
};

// Internal Functions

const matchWithPseudoBoundary = (regEx, str) => {
  const array = str.match(regEx);
  if (array) {
    return {
      index: array.index + array[1] ? array[1].length : 0,
      length: array[2] ? array[2].length : 0,
      0: array[2],
      1: array[3]
    };
  } else {
    return null;
  }
};

const extractEraFromString = function (calendar, str) {
  const calendarRegEx = calendarRegularExpressions(calendar);
  const eras = getErasFromCalendar(calendar);
  const match = matchWithPseudoBoundary(calendarRegEx.eraNameWithDigitRegExp, str);
  if (match) {
    const matchName = match[1];
    const matchLower = matchName.toLowerCase();
    const eraIndex = eras.findIndex(
      e => e.name.toLowerCase() === matchLower || e.shortName.toLowerCase() === matchLower
    );

    const matchIndex = match.index + match[0].length;
    const matchLength = matchName.length;
    const remainingStr = replaceWithSpaces(str, matchIndex, matchLength);
    return {
      eraIndex,
      remainingStr
    };
  }
  return null;
};

const extractTimeFromString = function (calendar, str) {
  let result = extractAmPmTimeFromString(calendar, str);
  if (!result) {
    result = extract24HourTimeFromString(calendar, str);
  }
  if (!result) {
    result = extractMilitaryTimeFromString(calendar, str);
  }
  if (!result) {
    result = extractMidnightFromString(calendar, str);
  }
  if (!result) {
    result = extractNoonFromString(calendar, str);
  }
  return result;
};

const extractAmPmTimeFromString = function (calendar, str) {
  const calendarRegEx = calendarRegularExpressions(calendar);
  const hoursInDay = getHoursInDayFromCalendar(calendar);

  if (hoursInDay % 2 === 0) {
    const arr = str.match(calendarRegEx.timeRegExp);
    if (arr) {
      let hour = parseInt(arr[1]);
      let minute = 0;
      let second = 0;
      let precision = Precision.Hour;
      if (!isNil(arr[3])) {
        minute = parseInt(arr[3]);
        precision = Precision.Minute;
      }
      if (!isNil(arr[5])) {
        second = parseInt(arr[5]);
        precision = Precision.Second;
      }
      if (!isNil(arr[6])) {
        if (arr[6].toLowerCase() === "am" || arr[6].toLowerCase() === "a" || arr[6].toLowerCase() === "a.m.") {
          if (hour === hoursInDay / 2) {
            hour = 0;
          }
        } else if (arr[6].toLowerCase() === "pm" || arr[6].toLowerCase() === "p" || arr[6].toLowerCase() === "p.m.") {
          if (hour < hoursInDay / 2) {
            hour += hoursInDay / 2;
          }
        }
      }

      const matchIndex = arr.index;
      const matchLength = arr[0].length;
      const remainingStr = replaceWithSpaces(str, matchIndex, matchLength);

      if ((hour >= 0 && hour < hoursInDay) || (hour === hoursInDay && minute === 0 && second === 0)) {
        return {
          hour,
          minute,
          second,
          precision,
          remainingStr
        };
      }
    }
  }
  // Time was not valid, so get out of here
  return null;
};

/**
 * Note: the regex can only extract a time has at least hour and minute.
 * So the output precision will always be >= Minute
 * @param {CalendarRecord} calendar
 * @param {string} str input string
 */
const extract24HourTimeFromString = function (calendar, str) {
  const calendarRegEx = calendarRegularExpressions(calendar);
  const hoursInDay = getHoursInDayFromCalendar(calendar);
  const arr = str.match(calendarRegEx.timeRegExp2);
  if (arr) {
    let hour = parseInt(arr[1]);
    let minute = 0;
    let second = 0;
    let precision = Precision.Hour;
    if (!isNil(arr[2])) {
      minute = parseInt(arr[2]);
      precision = Precision.Minute;
    }
    if (!isNil(arr[4])) {
      second = parseInt(arr[4]);
      precision = Precision.Second;
    }
    const matchIndex = arr.index;
    const matchLength = arr[0].length;
    const remainingStr = replaceWithSpaces(str, matchIndex, matchLength);

    if ((hour >= 0 && hour < hoursInDay) || (hour === hoursInDay && minute === 0 && second === 0)) {
      return {
        hour,
        minute,
        second,
        precision,
        remainingStr
      };
    }
  }
  // Time was not valid, so get out of here
  return null;
};

const extractMilitaryTimeFromString = function (calendar, str) {
  const calendarRegEx = calendarRegularExpressions(calendar);
  const hoursInDay = getHoursInDayFromCalendar(calendar);
  const arr = str.match(calendarRegEx.timeRegExp3);
  if (arr) {
    let hour = parseInt(arr[1]);
    let minute = 0;
    let second = 0;
    let precision = Precision.Hour;
    if (!isNil(arr[2])) {
      minute = parseInt(arr[2]);
      precision = Precision.Minute;
    }
    const matchIndex = arr.index;
    const matchLength = arr[0].length;
    const remainingStr = replaceWithSpaces(str, matchIndex, matchLength);

    if ((hour >= 0 && hour < hoursInDay) || (hour === hoursInDay && minute === 0 && second === 0)) {
      return {
        hour,
        minute,
        second,
        precision,
        remainingStr
      };
    }
  }
  // Time was not valid, so get out of here
  return null;
};

const extractMidnightFromString = function (calendar, str) {
  const hoursInDay = getHoursInDayFromCalendar(calendar);

  // Look for midnight
  const midnightStr = "midnight";
  const matchIndex = str.toLowerCase().indexOf(midnightStr);
  if (matchIndex !== -1) {
    const matchLength = midnightStr.length;
    const remainingStr = replaceWithSpaces(str, matchIndex, matchLength);
    return {
      hour: hoursInDay,
      minute: 0,
      second: 0,
      precision: Precision.Hour,
      remainingStr
    };
  }
  return null;
};

const extractNoonFromString = function (calendar, str) {
  const hoursInDay = getHoursInDayFromCalendar(calendar);

  // Look for noon
  if (hoursInDay % 2 === 0) {
    const noonStr = "noon";
    const matchIndex = str.toLowerCase().indexOf(noonStr);
    if (matchIndex !== -1) {
      const matchLength = noonStr.length;
      const remainingStr = replaceWithSpaces(str, matchIndex, matchLength);
      return {
        hour: hoursInDay / 2,
        minute: 0,
        second: 0,
        precision: Precision.Hour,
        remainingStr
      };
    }
  }
  return null;
};

/**
 *
 * @param {string} yearStr
 */
const getYearByAutoCorrect = (yearStr, _options) => {
  let year = null;
  if (yearStr) {
    year = parseInt(yearStr);
    const strLength = yearStr.length;
    if (strLength === 1) {
      year += 2000;
    } else if (strLength === 2) {
      const initNum = parseInt(yearStr.charAt(0));
      year = year + (initNum < 5 ? 2000 : 1900);
    }
  }
  return year;
};

const extractDateFromString = function (calendar, str, autoCorrection = false, era, explicitEra) {
  let result = extractLongDateFromString(calendar, str, autoCorrection, era, explicitEra);
  if (!result) {
    result = extractLargeYearFromString(calendar, str);
  }
  if (!result) {
    result = extractYearFromString(calendar, str, autoCorrection);
  }
  if (!result) {
    result = extractShortDateFromString(calendar, str, autoCorrection, era);
  }
  if (!result) {
    result = extractYearMonthFromString(calendar, str, autoCorrection);
  }
  if (!result) {
    result = extractMonthDateFromString(calendar, str, autoCorrection, era);
  }
  if (!result) {
    result = extractTextDateFromString(calendar, str);
  }

  return result;
};

const extractLongDateFromString = function (calendar, str, autoCorrection = false, era, explicitEra = false) {
  const calendarRegEx = calendarRegularExpressions(calendar);
  const months = getMonthsFromCalendar(calendar);
  const arr = matchWithPseudoBoundary(calendarRegEx.monthNameRegExp, str);

  if (arr) {
    let year = 0;
    let month = 1;
    let day = 1;
    let precision = Precision.Year;
    const matchName = String(arr[0]);
    const matchLower = matchName.toLowerCase();
    const monthIndex = months.findIndex(
      m => m.name.toLowerCase() === matchLower || m.shortName.toLowerCase() === matchLower
    );
    const matchIndex = arr.index;
    const matchLength = matchName.length;

    month = monthIndex + 1;
    let remainingStr = replaceWithSpaces(str, matchIndex, matchLength);

    const arr2 = remainingStr.match(calendarRegEx.monthNameRemainderRegExp);
    if (arr2) {
      // For removing thousand seperators (e.g. 1,000,000)
      let thousandSeperatorRegex =
        calendar.thousandSeparatorFormat === ThousandSeparatorFormatNames.Period ? /\./g : /,/g;

      if (!isNil(arr2[6]) && !isNil(arr2[1])) {
        // We're working with a month name and two numbers
        day = parseInt(arr2[1]);
        year = autoCorrection
          ? getYearByAutoCorrect(arr2[6].replace(thousandSeperatorRegex, ""))
          : parseInt(arr2[6].replace(thousandSeperatorRegex, ""));
        precision = Precision.Day;

        // Check if the day value is valid
        const isLeapYear = isLeapYearFromCalendar(calendar, era, year);
        const dayCount = !isLeapYear
          ? getNormalDurationForMonth(calendar, month)
          : getLeapDurationForMonth(calendar, month);

        if (day > dayCount) {
          // Return if this is an impossible date
          return null;
        }
      } else {
        // We're working with a month name and one number
        const currentYear = new Date().getFullYear();
        const isLeapYear = isLeapYearFromCalendar(calendar, era, currentYear);

        let dayCount = !isLeapYear
          ? getNormalDurationForMonth(calendar, month)
          : getLeapDurationForMonth(calendar, month);

        if (parseInt(arr2[1]) <= dayCount && !explicitEra) {
          // If the number is a valid day for the month, then extract as month + day
          day = parseInt(arr2[1]);
          year = currentYear;
          precision = Precision.Day;
        } else {
          // If the era was explicitly specified OR the number is not a valid day for the month, then extract as month + year
          year = autoCorrection
            ? getYearByAutoCorrect(arr2[1].replace(thousandSeperatorRegex, ""))
            : parseInt(arr2[1].replace(thousandSeperatorRegex, ""));
          precision = Precision.Month;
        }
      }
      remainingStr = replaceWithSpaces(remainingStr, arr2.index, arr2[0].length);
      return {
        year,
        month,
        day,
        precision,
        remainingStr
      };
    }
  }
  return null;
};

const extractShortDateOfUnsortableFormat = (calendar, calendarRegEx, str, autoCorrection, era, monthCount) => {
  const arr = str.match(calendarRegEx.formalDateRegExp);

  let result = { year: null, month: null, day: null, partsArr: null };

  if (arr) {
    let d1 = arr[1];
    let d2 = arr[2];
    let d3 = arr[3];
    // Set up for dd/mm/yy and dd/mm/yy format
    let day = parseInt(d1);
    let month = parseInt(d2);
    let year = autoCorrection ? getYearByAutoCorrect(d3) : parseInt(d3);

    // Check for mm/dd/yy and mm/dd/yyyy format
    if (calendar.dateOrder === DateOrderIdentifiers.MonthDayYear) {
      day = parseInt(d2);
      month = parseInt(d1);
    }

    // Check if the day and month values are valid
    let isLeapYear = isLeapYearFromCalendar(calendar, era, year);

    const dayCount =
      month <= monthCount
        ? !isLeapYear
          ? getNormalDurationForMonth(calendar, month)
          : getLeapDurationForMonth(calendar, month)
        : null;

    if (dayCount !== null && day <= dayCount) {
      result.year = year;
      result.month = month;
      result.day = day;
      result.partsArr = arr;
    }
  }
  return result;
};

const extractShortDateOfSortableFormat = (calendar, calendarRegEx, str, autoCorrection, era, monthCount) => {
  const arr = str.match(calendarRegEx.formalDateRegExp);

  let result = { year: null, month: null, day: null, partsArr: null };

  if (arr) {
    let d1 = arr[1];
    let d2 = arr[2];
    let d3 = arr[3];

    let day = parseInt(d3);
    const month = parseInt(d2);
    const year = autoCorrection ? getYearByAutoCorrect(d1) : parseInt(d1);
    const isLeapYear = isLeapYearFromCalendar(calendar, era, year);

    const dayCount =
      month <= monthCount
        ? !isLeapYear
          ? getNormalDurationForMonth(calendar, month)
          : getLeapDurationForMonth(calendar, month)
        : null;

    if (dayCount !== null && day <= dayCount) {
      result.year = year;
      result.month = month;
      result.day = day;
      result.partsArr = arr;
    }
  }
  return result;
};

const isInvalidDate = (year, month, day) => year === null && month === null && day === null;

const extractShortDateFromString = function (calendar, str, autoCorrection = false, era) {
  const calendarRegEx = calendarRegularExpressions(calendar);
  const { dateOrder } = calendar;
  const monthCount = getMonthsFromCalendar(calendar).count();

  let day = null;
  let month = null;
  let year = null;
  let regexResult = null;

  if (dateOrder == DateOrderIdentifiers.MonthDayYear || dateOrder == DateOrderIdentifiers.DayMonthYear) {
    const { day: d, month: m, year: y, partsArr: p } = extractShortDateOfUnsortableFormat(
      calendar,
      calendarRegEx,
      str,
      autoCorrection,
      era,
      monthCount
    );
    day = d;
    month = m;
    year = y;
    regexResult = p;
  }
  if (dateOrder == DateOrderIdentifiers.YearMonthDay || isInvalidDate(year, month, day)) {
    const { day: d, month: m, year: y, partsArr: p } = extractShortDateOfSortableFormat(
      calendar,
      calendarRegEx,
      str,
      autoCorrection,
      era,
      monthCount
    );
    day = d;
    month = m;
    year = y;
    regexResult = p;
  }

  if (isInvalidDate(year, month, day)) {
    return null;
  }

  let precision = Precision.Day;

  const matchIndex = regexResult.index;
  const matchLength = regexResult[0].length;

  let remainingStr = replaceWithSpaces(str, matchIndex, matchLength);

  return {
    year,
    month,
    day,
    precision,
    remainingStr
  };
};

const extractMonthDateFromString = function (calendar, str, autoCorrection = false, era) {
  const calendarRegEx = calendarRegularExpressions(calendar);
  const arr = str.match(calendarRegEx.monthDateRegExp);

  if (arr) {
    let d1 = parseInt(arr[1]);
    let d2 = parseInt(arr[2]);

    let precision = Precision.Day;

    // Set up for dd/mm format
    let day = d1;
    let month = d2;
    let year = new Date().getFullYear();

    // Check for mm/dd format
    if (calendar.dateOrder === DateOrderIdentifiers.MonthDayYear) {
      day = d2;
      month = d1;
    }

    // Check if the day and month values are valid
    const isLeapYear = isLeapYearFromCalendar(calendar, era, year);
    const monthCount = getMonthsFromCalendar(calendar).count();

    const dayCount =
      month <= monthCount
        ? !isLeapYear
          ? getNormalDurationForMonth(calendar, month)
          : getLeapDurationForMonth(calendar, month)
        : null;

    // Otherwise, try year and month
    // Use mm/yy or yy/mm format
    if (dayCount === null || day > dayCount) {
      precision = Precision.Month;

      day = 1;
      month = d1;
      year = d2;

      if (month > monthCount && year > monthCount) {
        // Return if this is an impossible date
        return null;
      } else if (month > monthCount) {
        // Switch year and month if needed
        month = d2;
        year = d1;
      }
    }

    // Apply year autocorrection
    year = autoCorrection ? getYearByAutoCorrect(year) : year;

    const matchIndex = arr.index;
    const matchLength = arr[0].length;

    let remainingStr = replaceWithSpaces(str, matchIndex, matchLength);

    return {
      year,
      month,
      day,
      precision,
      remainingStr
    };
  }

  return null;
};

const extractYearMonthFromString = function (calendar, str, autoCorrection = false) {
  if (calendar.dateOrder === DateOrderIdentifiers.YearMonthDay) {
    const calendarRegEx = calendarRegularExpressions(calendar);
    const arr = str.match(calendarRegEx.yearMonthRegExp);
    if (arr) {
      let d1 = parseInt(arr[1]);
      let d2 = parseInt(arr[2]);

      let precision = Precision.Month;

      // Set up for yy-mm format
      let year = autoCorrection ? getYearByAutoCorrect(arr[1]) : d1;

      let month = d2;

      // check month value is valid
      const monthCount = getMonthsFromCalendar(calendar).count();
      if (month > monthCount) {
        return null;
      }

      const matchIndex = arr.index;
      const matchLength = arr[0].length;

      let remainingStr = replaceWithSpaces(str, matchIndex, matchLength);

      return {
        year,
        month,
        day: 1,
        precision,
        remainingStr
      };
    }
  }

  return null;
};

const extractLargeYearFromString = function (calendar, str) {
  const calendarRegEx = calendarRegularExpressions(calendar);
  let year = 0;
  const precision = Precision.Year;
  const month = 1;
  const day = 1;

  const arr = str.match(calendarRegEx.largeYearRegExp);
  if (arr) {
    // For removing thousand seperators (e.g. 1,000,000)
    let thousandSeperatorRegex =
      calendar.thousandSeparatorFormat === ThousandSeparatorFormatNames.Period ? /\./g : /,/g;

    const multiple = arr.length > 9 ? arr[9].toLowerCase() : "million";
    if (multiple === "billion") {
      // Replace thousand seperators, then turn any remaining comma (which will be used as a decimal point) into a period
      year = parseFloat(arr[1].replace(thousandSeperatorRegex, "").replace(",", ".")) * 1000000000;
    } else if (multiple === "million") {
      year = parseFloat(arr[1].replace(thousandSeperatorRegex, "").replace(",", ".")) * 1000000;
    }
    const remainingStr = replaceWithSpaces(str, arr.index, arr[0].length);
    return {
      year,
      month,
      day,
      precision,
      remainingStr
    };
  }
  return null;
};

const extractYearFromString = function (calendar, str, autoCorrection = false) {
  const calendarRegEx = calendarRegularExpressions(calendar);
  let year = 0;
  const precision = Precision.Year;
  const month = 1;
  const day = 1;

  const arr = str.match(calendarRegEx.yearRegExp);
  if (arr) {
    // For removing thousand seperators (e.g. 1,000,000)
    let thousandSeperatorRegex =
      calendar.thousandSeparatorFormat === ThousandSeparatorFormatNames.Period ? /\./g : /,/g;

    year = autoCorrection
      ? getYearByAutoCorrect(arr[1].replace(thousandSeperatorRegex, ""))
      : parseInt(arr[1].replace(thousandSeperatorRegex, ""));
    let remainingStr = replaceWithSpaces(str, arr.index, arr[0].length);

    // Return if there's any extra text besides punctuation
    remainingStr = remainingStr.replace(/,/, "");
    remainingStr = remainingStr.replace(/\./, "");
    remainingStr = remainingStr.replace(/\+/, "");
    const trimmedRemainingStr = remainingStr.trim();
    if (trimmedRemainingStr) {
      return null;
    }

    return {
      year,
      month,
      day,
      precision,
      remainingStr
    };
  }

  return null;
};

export const extractDateFromYearString = function (calendar, str) {
  let remainingStr = str;
  const eras = getErasFromCalendar(calendar);
  const eraResult = extractEraFromString(calendar, remainingStr);
  const era = eraResult ? eraResult.eraIndex : eras.size - 1;
  remainingStr = eraResult ? eraResult.remainingStr : remainingStr;
  const shouldAutoCorrect = calendar.defaultToModernCentury && !eraResult;

  let result = extractLongDateFromString(calendar, remainingStr, shouldAutoCorrect, era, eraResult);
  if (!result) {
    result = extractLargeYearFromString(calendar, remainingStr);
  }
  if (!result) {
    result = extractYearFromString(calendar, remainingStr, shouldAutoCorrect);
  }

  return result ? dateWithComponents(calendar, era, result.year, 1, 1, 0, 0, 0, Precision.year) : null;
};

const extractTextDateFromString = function (calendar, str) {
  let trimmedStr = str.trim();
  let date = new Date();

  let precision = Precision.Day;
  let day = 1;
  let month = 1;
  let year = 0;

  switch (trimmedStr) {
    case "today":
      day = date.getDate();
      month = date.getMonth() + 1;
      year = date.getFullYear();
      break;
    case "tomorrow":
      date.setDate(date.getDate() + 1);
      day = date.getDate();
      month = date.getMonth() + 1;
      year = date.getFullYear();
      break;
    case "yesterday":
      date.setDate(date.getDate() - 1);
      day = date.getDate();
      month = date.getMonth() + 1;
      year = date.getFullYear();
      break;
    default:
      return null;
  }

  return {
    year,
    month,
    day,
    precision
  };
};

const extractWeekFromString = (calendar, str) => {
  const calendarRegEx = calendarRegularExpressions(calendar);
  const array = str.match(calendarRegEx.weekRegExp);
  let week = null;
  if (array && array.length > 4) {
    week = parseInt(array[4]);
    const remainingStr = replaceWithSpaces(str, array.index, array[0].length);
    return {
      week,
      remainingStr
    };
  }
  return null;
};

const extractWeekdayFromPartialString = (calendar, str) => {
  const calendarRegEx = calendarRegularExpressions(calendar);
  const array = str.match(calendarRegEx.weekdayNameRegExp);

  if (array) {
    const matchName = array[2];
    const matchNameLower = matchName.toLowerCase();
    const weekdayIndex = calendar.weekdays.findIndex(
      weekday => weekday.name.toLowerCase() === matchNameLower || weekday.shortName.toLowerCase() === matchNameLower
    );
    const remainingStr = str.replace(matchName, replaceWithSpaces(matchName, 0, matchName.length));
    return {
      weekdayIndex,
      remainingStr
    };
  }
  return null;
};

export const extractDayFromString = (calendar, str) => {
  const calendarRegEx = calendarRegularExpressions(calendar);
  const array = str.match(calendarRegEx.dayRegExp);

  if (array && !isNil(array[4]) && !isNil(array[1])) {
    const day = parseInt(array[4]);
    const remainingStr = replaceWithSpaces(str, array.index, array[0].length);
    return {
      day,
      remainingStr
    };
  }
  return null;
};

const extractRelativeTimeFromString = (calendar, str) => {
  const calendarRegEx = calendarRegularExpressions(calendar);
  const array = str.match(calendarRegEx.timeRegExp4);
  let hour = null;
  let minute = null;
  let second = null;
  let negative = false;
  let precision = Precision.Auto;

  if (array && !isNil(array[2])) {
    hour = parseInt(array[2]);
    const negation = array[1];
    if (!isNil(negation)) {
      if (negation === "-") {
        negative = true;
      } else if (negation === "+") {
        negative = false;
      } else {
        negative = false;
      }
    }
    precision = Precision.Hour;

    if (!isNil(array[5])) {
      minute = parseInt(array[5]);
      precision = Precision.Minute;
    } else {
      minute = 0;
    }
    if (!isNil(array[7])) {
      second = parseInt(array[7]);
      precision = Precision.Second;
    } else {
      second = 0;
    }
    const remainingStr = replaceWithSpaces(str, array.index, array[0].length);
    return {
      negative,
      precision,
      hour,
      minute,
      second,
      remainingStr
    };
  }
  return null;
};

const testFunctions = {
  extractWeekFromString,
  extractDayFromString,
  extractRelativeTimeFromString,
  extractWeekdayFromPartialString,
  getYearByAutoCorrect
};

export { testFunctions };

// export default {
//   dateWithString,
//   endDateWithString
// };
